/**
 * Score Trend Chart Component
 * CSS-based bar chart showing evaluation score trends
 * No external charting library required
 */

import { useMemo } from 'react';
import { useI18n } from '@/i18n';

interface ScoreDataPoint {
  score: number;
  passed: boolean;
  date: string;
}

interface ScoreTrendChartProps {
  data: ScoreDataPoint[];
  maxItems?: number;
}

export function ScoreTrendChart({ data, maxItems = 10 }: ScoreTrendChartProps) {
  const { messages } = useI18n();
  const t = messages.evalScenarios;

  // Take the most recent N items and reverse for chronological display
  const chartData = useMemo(() => {
    const items = data.slice(0, maxItems).reverse();
    return items;
  }, [data, maxItems]);

  if (chartData.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4 text-sm">
        {t.historyPanel?.noHistory || '暂无评测记录'}
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('zh-CN', {
      month: 'numeric',
      day: 'numeric',
    }).format(date);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{t.historyPanel?.scoreTrend || '分数趋势'}</span>
        <span>
          {t.historyPanel?.recentRuns?.replace('{n}', String(chartData.length)) ||
           `最近 ${chartData.length} 次`}
        </span>
      </div>

      {/* Bar Chart */}
      <div className="flex items-end gap-1 h-16">
        {chartData.map((item, index) => {
          const height = Math.max(item.score, 5); // Minimum 5% height for visibility
          const bgColor = item.passed
            ? 'bg-green-500 hover:bg-green-600'
            : 'bg-red-500 hover:bg-red-600';

          return (
            <div
              key={index}
              className="flex-1 flex flex-col items-center group relative"
            >
              {/* Bar */}
              <div
                className={`w-full rounded-t transition-all duration-200 ${bgColor}`}
                style={{ height: `${height}%` }}
              />

              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                  <div className="font-medium">{item.score.toFixed(1)}%</div>
                  <div className="text-gray-300">{formatDate(item.date)}</div>
                </div>
                <div className="w-2 h-2 bg-gray-900 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1" />
              </div>
            </div>
          );
        })}
      </div>

      {/* X-axis labels (show first and last date) */}
      <div className="flex justify-between text-[10px] text-gray-400">
        <span>{formatDate(chartData[0]?.date || '')}</span>
        {chartData.length > 1 && (
          <span>{formatDate(chartData[chartData.length - 1]?.date || '')}</span>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-green-500 rounded" />
          {t.passed || '通过'}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-red-500 rounded" />
          {t.failed || '失败'}
        </span>
      </div>
    </div>
  );
}
